import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Badge,
  Divider,
  Tabs,
  Space,
  Typography,
  Upload,
  Row,
  Col,
  Collapse,
  Checkbox,
  Spin,
  Alert,
  InputNumber
} from 'antd';
import {
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  CopyOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { toast } from 'sonner';
import { FEED_TEMPLATES } from './feedTemplates';
import { apiRequests } from "src/shared/api/api";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { TextArea } = Input;

const FeedForm = ({ token, feed, product, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [fieldTags, setFieldTags] = useState([]);
  const [previewXml, setPreviewXml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [priceTypes, setPriceTypes] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('simple');
  const [jsonInput, setJsonInput] = useState('');
  const [jsonValid, setJsonValid] = useState(true);
  const [copied, setCopied] = useState(false);

  // Наблюдаем за критериями для подсчета активных фильтров
  const criteria = Form.useWatch('criteria', form);
  
  // Подсчет активных фильтров
  const filterCount = useMemo(() => {
    let count = 0;
    if (criteria) {
      // Проверяем только_в_наличии
      if (criteria.only_on_stock === true) {
        count++;
      }
      
      // Проверяем склады
      if (criteria.warehouse_id && Array.isArray(criteria.warehouse_id) && criteria.warehouse_id.length > 0) {
        count++;
      }
      
      // Проверяем категории
      if (criteria.category_id && Array.isArray(criteria.category_id) && criteria.category_id.length > 0) {
        count++;
      }
      
      // Проверяем тип цены
      if (criteria.price_types_id) {
        count++;
      }
      
      // Проверяем диапазон цен
      if ((criteria.prices && criteria.prices.from) || (criteria.prices && criteria.prices.to)) {
        count++;
      }
    }
    return count;
  }, [criteria]);

  const dataSource = product || {
    id: 110,
    name: "Бальзам для губ «Чёрный орех»",
    description_short: "Состав: масляный экстракт лечебной грязи озера Тамбукан, экстракт околоплодников Чёрного ореха, прополис, масло кориандра, масло тыквы, масло мелиссы, воск пчелиный.",
    code: "БДГ«О_209742_209742",
    category: 4,
    prices: [{ price: 11000, price_type: "chatting", is_main: true }],
    balances: [{ current_amount: 15 }],
    attributes: [
      { name: "Вес", value: "30 г" },
    ],
    photos: [{
      public_url: "https://dev.tablecrm.com/api/v1/pictures/133/content"
    }],
  };

  useEffect(() => {
    const loadFilterData = async () => {
      setLoadingFilters(true);
      try {
        const [warehousesRes, categoriesRes, priceTypesRes] = await Promise.all([
          apiRequests.warehouses.getItems({ limit: 100 }),
          apiRequests.categories.getItems({ limit: 100 }),
          apiRequests.priceTypes.getItems(),
        ]);
        
        setWarehouses(warehousesRes.data?.result || warehousesRes.data || []);
        setCategories(categoriesRes.data?.result || categoriesRes.data || []);
        setPriceTypes(priceTypesRes.data?.result || priceTypesRes.data || []);
      } catch (error) {
        console.error('Ошибка загрузки данных для фильтров:', error);
        toast.error('Не удалось загрузить данные для фильтров');
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilterData();
  }, []);

  useEffect(() => {
    if (feed) {
      const tags = Object.entries(feed.field_tags || {}).map(([xmlTag, field]) => ({
        xmlTag,
        field
      }));
      setFieldTags(tags);
      
      form.setFieldsValue({
        name: feed.name,
        description: feed.description || '',
        root_tag: feed.root_tag,
        item_tag: feed.item_tag,
        criteria: {
          only_on_stock: feed.criteria?.only_on_stock ?? true,
          warehouse_id: feed.criteria?.warehouse_id || [],
          category_id: feed.criteria?.category_id || [],
          price_types_id: feed.criteria?.price_types_id,
          prices: {
            from: feed.criteria?.prices?.from,
            to: feed.criteria?.prices?.to,
          },
        },
      });

      const jsonData = {
        name: feed.name,
        description: feed.description || '',
        root_tag: feed.root_tag,
        item_tag: feed.item_tag,
        field_tags: feed.field_tags || {},
        criteria: feed.criteria || {
          only_on_stock: true,
          warehouse_id: [],
          category_id: [],
          price_types_id: undefined,
          prices: { from: undefined, to: undefined },
        },
        tilda_url: feed.tilda_url || '',
        tilda_username: feed.tilda_username || '',
        tilda_password: feed.tilda_password || '',
        tilda_sync_enabled: feed.tilda_sync_enabled || false,
        tilda_sync_interval: feed.tilda_sync_interval || 0,
        tilda_price_id: feed.tilda_price_id || '',
        tilda_discount_price_id: feed.tilda_discount_price_id || '',
        tilda_catalog_id: feed.tilda_catalog_id || '',
        tilda_warehouse_id: feed.tilda_warehouse_id || ''
      };
      setJsonInput(JSON.stringify(jsonData, null, 2));
    } else if (product) {
      form.setFieldsValue({
        name: `Фид для ${product.name}`,
        description: '',
        root_tag: 'yml_catalog',
        item_tag: 'offer',
        criteria: {
          only_on_stock: true,
          warehouse_id: [],
          category_id: [],
          price_types_id: undefined,
          prices: {
            from: undefined,
            to: undefined,
          },
        },
      });
      setFieldTags([
        { xmlTag: 'id', field: 'id' },
        { xmlTag: 'name', field: 'name' },
        { xmlTag: 'price', field: 'price' },
        { xmlTag: 'currencyId', field: 'RUB' },
        { xmlTag: 'categoryId', field: 'category' },
        { xmlTag: 'picture', field: 'photos' },
        { xmlTag: 'description', field: 'description_short' },
        { xmlTag: 'available', field: 'current_amount' },
      ]);

      const defaultJson = {
        name: `Фид для ${product.name}`,
        description: '',
        root_tag: 'yml_catalog',
        item_tag: 'offer',
        field_tags: {
          'id': 'id',
          'name': 'name',
          'price': 'price',
          'currencyId': 'RUB',
          'categoryId': 'category',
          'picture': 'photos',
          'description': 'description_short',
          'available': 'current_amount'
        },
        criteria: {
          only_on_stock: true,
          warehouse_id: [],
          category_id: [],
          price_types_id: undefined,
          prices: { from: undefined, to: undefined }
        },
        tilda_url: '',
        tilda_username: '',
        tilda_password: '',
        tilda_sync_enabled: false,
        tilda_sync_interval: 0,
        tilda_price_id: '',
        tilda_discount_price_id: '',
        tilda_catalog_id: '',
        tilda_warehouse_id: ''
      };
      setJsonInput(JSON.stringify(defaultJson, null, 2));
    } else {
      // Инициализация для пустого фида
      form.setFieldsValue({
        name: '',
        description: '',
        root_tag: 'yml_catalog',
        item_tag: 'offer',
        criteria: {
          only_on_stock: true,
          warehouse_id: [],
          category_id: [],
          price_types_id: undefined,
          prices: {
            from: undefined,
            to: undefined,
          },
        },
      });
      setFieldTags([]);
      
      const defaultJson = {
        name: '',
        description: '',
        root_tag: 'yml_catalog',
        item_tag: 'offer',
        field_tags: {},
        criteria: {
          only_on_stock: true,
          warehouse_id: [],
          category_id: [],
          price_types_id: undefined,
          prices: { from: undefined, to: undefined }
        },
        tilda_url: '',
        tilda_username: '',
        tilda_password: '',
        tilda_sync_enabled: false,
        tilda_sync_interval: 0,
        tilda_price_id: '',
        tilda_discount_price_id: '',
        tilda_catalog_id: '',
        tilda_warehouse_id: ''
      };
      setJsonInput(JSON.stringify(defaultJson, null, 2));
    }
  }, [feed, product, form]);

  const escapeXml = (unsafe) => {
    if (unsafe == null || unsafe === undefined) return '';
    return String(unsafe).replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '&': return '&amp;';
        case "'": return '&apos;';
        case '"': return '&quot;';
        default: return c;
      }
    });
  };

  const getPathValue = (obj, path) => {
    if (!path) return undefined;
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
    let current = obj;
    for (const part of parts) {
      if (current == null) return undefined;
      current = current[part];
    }
    return current;
  };

  const resolveValue = (data, field) => {
    const simpleMap = {
      'id': 'id',
      'name': 'name',
      'code': 'code',
      'description': 'description_short',
      'description_short': 'description_short',
      'description_long': 'description_long',
      'category': 'category',
      'unit_name': 'unit_name',
    };

    if (simpleMap[field]) {
      return getPathValue(data, simpleMap[field]);
    }

    if (field === 'price') {
      const prices = data.prices || [];
      const main = prices.find(p => p.is_main) || prices[0];
      return main ? main.price / 100 : undefined;
    }

    if (field === 'current_amount') {
      const balances = data.balances || [];
      return balances.reduce((sum, b) => sum + (b.current_amount || b.amount || 0), 0);
    }

    if (field === 'photos' || field === 'images') {
      return (data.photos || []).map(p => p.public_url || '').filter(Boolean);
    }

    if (field === 'attributes') {
      return data.attributes || [];
    }

    if (!field.includes('.') && !field.includes('[')) {
      return field;
    }

    return getPathValue(data, field);
  };

  const generateClientPreview = (values) => {
    const root_tag = values.root_tag || 'yml_catalog';
    const item_tag = values.item_tag || 'offer';

    const fieldTagsObj = {};
    fieldTags.forEach(tag => {
      if (tag.xmlTag && tag.field) {
        fieldTagsObj[tag.xmlTag] = tag.field;
      }
    });

    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<${root_tag}>\n`;
    xml += `  <${item_tag}>\n`;

    Object.entries(fieldTagsObj).forEach(([xmlTag, productField]) => {
      let value = resolveValue(dataSource, productField);

      if (value === undefined) return;

      const isParamTag = xmlTag.toLowerCase() === 'param';
      const isImageArray = Array.isArray(value) && value.every(v => typeof v === 'string' && v.startsWith('http'));

      if (isParamTag && Array.isArray(value) && value[0]?.name !== undefined) {
        value.forEach(attr => {
          const attrName = escapeXml(attr.name || attr.alias || '');
          const attrValue = escapeXml(attr.value || '');
          xml += `    <${xmlTag} name="${attrName}">${attrValue}</${xmlTag}>\n`;
        });
      } else if (isImageArray || (xmlTag.toLowerCase().includes('picture') || xmlTag.toLowerCase().includes('image'))) {
        value.forEach(url => {
          xml += `    <${xmlTag}>${escapeXml(url)}</${xmlTag}>\n`;
        });
      } else if (Array.isArray(value)) {
        value.forEach(v => {
          xml += `    <${xmlTag}>${escapeXml(v)}</${xmlTag}>\n`;
        });
      } else {
        xml += `    <${xmlTag}>${escapeXml(value)}</${xmlTag}>\n`;
      }
    });

    xml += `  </${item_tag}>\n`;
    xml += `</${root_tag}>`;

    return xml;
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    setPreviewXml('');

    if (feed?.url_token) {
      try {
        const response = await apiRequests.feeds.getFeedXml(feed.url_token);
        setPreviewXml(response.data);
      } catch (error) {
        setPreviewXml('Ошибка загрузки актуального XML с сервера');
        toast.error('Ошибка загрузки XML');
      }
    } else {
      const values = form.getFieldsValue();
      const xml = generateClientPreview(values);
      setPreviewXml(xml);
    }

    setLoadingPreview(false);
  };

  const handleJsonChange = (value) => {
    setJsonInput(value);
    try {
      JSON.parse(value);
      setJsonValid(true);
    } catch (e) {
      setJsonValid(false);
    }
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const parsed = JSON.parse(content);
        setJsonInput(JSON.stringify(parsed, null, 2));
        setJsonValid(true);
        toast.success('JSON успешно загружен из файла');
      } catch (error) {
        toast.error('Ошибка чтения JSON файла');
      }
    };
    reader.readAsText(file);
    return false; // Отменяем автоматическую загрузку
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(jsonInput);
    setCopied(true);
    toast.success('JSON скопирован в буфер обмена');
    setTimeout(() => setCopied(false), 2000);
  };

  const updateJsonFromForm = () => {
    const formValues = form.getFieldsValue();
    const fieldTagsObj = {};
    fieldTags.forEach(tag => {
      if (tag.xmlTag && tag.field) {
        fieldTagsObj[tag.xmlTag] = tag.field;
      }
    });

    const jsonData = {
      name: formValues.name,
      description: formValues.description || '',
      root_tag: formValues.root_tag,
      item_tag: formValues.item_tag,
      field_tags: fieldTagsObj,
      criteria: formValues.criteria || {
        only_on_stock: true,
        warehouse_id: [],
        category_id: [],
        price_types_id: undefined,
        prices: { from: undefined, to: undefined },
      },
      tilda_url: '',
      tilda_username: '',
      tilda_password: '',
      tilda_sync_enabled: false,
      tilda_sync_interval: 0,
      tilda_price_id: '',
      tilda_discount_price_id: '',
      tilda_catalog_id: '',
      tilda_warehouse_id: ''
    };

    setJsonInput(JSON.stringify(jsonData, null, 2));
    setJsonValid(true);
  };

  const submitJsonFeed = async () => {
    if (!jsonValid) {
      toast.error('Невалидный JSON. Исправьте ошибки перед сохранением');
      return;
    }

    setIsSubmitting(true);
    const toastId = toast.loading(feed ? 'Сохранение фида...' : 'Создание фида...');
    
    try {
      const payload = JSON.parse(jsonInput);
      
      let savedFeed = null;

      if (feed) {
        await apiRequests.feeds.patch(feed.id, payload);
        savedFeed = { ...feed, ...payload };
        
        toast.success('Фид успешно обновлен!', { id: toastId });
        
        setTimeout(() => {
          onSuccess(savedFeed);
        }, 800);
      } else {
        const response = await apiRequests.feeds.post(payload);
        savedFeed = response.data;
        
        toast.success('Фид успешно создан!', { id: toastId });
        
        setTimeout(() => {
          onSuccess(savedFeed);
        }, 800);
      }

    } catch (error) {
      console.error('Error saving feed:', error);
      
      // Показываем сообщение от сервера при ошибке
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message ||
                          'Произошла ошибка при сохранении фида';
      
      toast.error(errorMessage, { 
        id: toastId,
        duration: 5000 
      });
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    const toastId = toast.loading(feed ? 'Сохранение фида...' : 'Создание фида...');
    
    try {
      const fieldTagsObj = {};
      fieldTags.forEach(tag => {
        if (tag.xmlTag && tag.field) {
          fieldTagsObj[tag.xmlTag] = tag.field;
        }
      });

      const criteria = {};
      const formCriteria = values.criteria || {};
      
      if (formCriteria.only_on_stock !== undefined) {
        criteria.only_on_stock = formCriteria.only_on_stock;
      }
      
      if (formCriteria.warehouse_id && formCriteria.warehouse_id.length > 0) {
        criteria.warehouse_id = formCriteria.warehouse_id;
      }
      
      if (formCriteria.category_id && formCriteria.category_id.length > 0) {
        criteria.category_id = formCriteria.category_id;
      }
      
      if (formCriteria.price_types_id) {
        criteria.price_types_id = formCriteria.price_types_id;
      }
      
      if (formCriteria.prices && (formCriteria.prices.from || formCriteria.prices.to)) {
        criteria.prices = {};
        if (formCriteria.prices.from) criteria.prices.from = formCriteria.prices.from;
        if (formCriteria.prices.to) criteria.prices.to = formCriteria.prices.to;
      }

      const payload = {
        name: values.name,
        description: values.description || '',
        root_tag: values.root_tag,
        item_tag: values.item_tag,
        field_tags: fieldTagsObj,
        criteria: Object.keys(criteria).length > 0 ? criteria : null,
      };

      let savedFeed = null;

      if (feed) {
        await apiRequests.feeds.patch(feed.id, payload);
        savedFeed = { ...feed, ...payload };
        
        toast.success('Фид успешно обновлен!', { id: toastId });
        
        setTimeout(() => {
          onSuccess(savedFeed);
        }, 800);
      } else {
        const response = await apiRequests.feeds.post(payload);
        savedFeed = response.data;
        
        toast.success('Фид успешно создан!', { id: toastId });
        
        setTimeout(() => {
          onSuccess(savedFeed);
        }, 800);
      }

    } catch (error) {
      console.error('Error saving feed:', error);
      
      // Показываем сообщение от сервера при ошибке
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error ||
                          error.message ||
                          'Произошла ошибка при сохранении фида';
      
      toast.error(errorMessage, { 
        id: toastId,
        duration: 5000 
      });
      
    } finally {
      setIsSubmitting(false);
    }
  };

  const addFieldTag = () => {
    setFieldTags([...fieldTags, { xmlTag: '', field: '' }]);
  };

  const updateFieldTag = (index, key, value) => {
    const newTags = [...fieldTags];
    newTags[index][key] = value;
    setFieldTags(newTags);
  };

  const removeFieldTag = (index) => {
    setFieldTags(fieldTags.filter((_, i) => i !== index));
  };

  const handleTemplateChange = (value) => {
    if (value) {
      const tmpl = FEED_TEMPLATES[value];
      form.setFieldsValue({
        name: form.getFieldValue('name') || tmpl.name,
        description: tmpl.description || '',
        root_tag: tmpl.root_tag,
        item_tag: tmpl.item_tag,
      });
      
      const tags = Object.entries(tmpl.field_tags || {}).map(([xmlTag, field]) => ({
        xmlTag,
        field
      }));
      setFieldTags(tags);

      // Обновляем JSON
      updateJsonFromForm();
    }
  };

  return (
    <Card
      title={
        <div className="flex justify-between items-center">
          <Title level={4} style={{ margin: 0 }}>
            {feed ? 'Редактирование фида' : 'Создание XML фида'}
          </Title>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
          />
        </div>
      }
      style={{ width: '100%' }}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Простой ввод" key="simple">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="Название фида"
                  name="name"
                  rules={[
                    { required: true, message: 'Название обязательно' },
                    { min: 2, message: 'Минимум 2 символа' }
                  ]}
                >
                  <Input placeholder="Например: Яндекс.Маркет" />
                </Form.Item>

                <Form.Item
                  label="Описание"
                  name="description"
                >
                  <TextArea 
                    placeholder="Описание назначения фида" 
                    rows={3}
                  />
                </Form.Item>

                <Form.Item
                  label="Корневой тег XML"
                  name="root_tag"
                  rules={[{ required: true, message: 'Корневой тег обязателен' }]}
                >
                  <Input placeholder="Например: yml_catalog" />
                </Form.Item>

                <Form.Item
                  label="Тег товара"
                  name="item_tag"
                  rules={[{ required: true, message: 'Тег товара обязателен' }]}
                >
                  <Input placeholder="Например: offer" />
                </Form.Item>

                <Form.Item label="Шаблон фида">
                  <Select
                    placeholder="Выберите готовый шаблон"
                    onChange={handleTemplateChange}
                  >
                    <Option value="YANDEX_MARKET">Яндекс.Маркет (YML)</Option>
                    <Option value="GOOGLE_SHOPPING">Google Shopping (RSS)</Option>
                    <Option value="WILDBERRIES">Wildberries</Option>
                    <Option value="OZON">Ozon</Option>
                    <Option value="FLOWWOW">Flowwow</Option>
                    <Option value="FACEBOOK_CATALOG">Facebook Product Catalog</Option>
                  </Select>
                  <Text type="secondary">
                    Выберите шаблон для быстрой настройки под популярные платформы
                  </Text>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Card
                  title={
                    <Space>
                      <span>Сопоставление полей</span>
                      <Badge count={fieldTags.length} />
                    </Space>
                  }
                  size="small"
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {fieldTags.map((tag, index) => (
                      <Row key={index} gutter={8} align="middle">
                        <Col span={11}>
                          <Input
                            placeholder="XML тег"
                            value={tag.xmlTag}
                            onChange={(e) => updateFieldTag(index, 'xmlTag', e.target.value)}
                          />
                        </Col>
                        <Col span={11}>
                          <Input
                            placeholder="Поле товара"
                            value={tag.field}
                            onChange={(e) => updateFieldTag(index, 'field', e.target.value)}
                          />
                        </Col>
                        <Col span={2}>
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => removeFieldTag(index)}
                          />
                        </Col>
                      </Row>
                    ))}
                    <Button
                      icon={<PlusOutlined />}
                      onClick={addFieldTag}
                      block
                    >
                      Добавить поле
                    </Button>
                  </Space>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* Секция фильтрации */}
            <Collapse>
              <Panel 
                header={
                  <Space>
                    <span>Фильтрация товаров</span>
                    <Badge count={filterCount} />
                  </Space>
                } 
                key="filters"
              >
                <Row gutter={16}>
                  <Col span={24}>
                    <Form.Item
                      name={['criteria', 'only_on_stock']}
                      valuePropName="checked"
                    >
                      <Checkbox>Только в наличии</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Склады"
                      name={['criteria', 'warehouse_id']}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Выберите склады"
                        loading={loadingFilters}
                        allowClear
                      >
                        {warehouses.map(warehouse => (
                          <Option key={warehouse.id} value={warehouse.id}>
                            {warehouse.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Категории"
                      name={['criteria', 'category_id']}
                    >
                      <Select
                        mode="multiple"
                        placeholder="Выберите категории"
                        loading={loadingFilters}
                        allowClear
                      >
                        {categories.map(category => (
                          <Option key={category.id} value={category.id}>
                            {category.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Тип цены"
                      name={['criteria', 'price_types_id']}
                    >
                      <Select
                        placeholder="Выберите тип цены"
                        loading={loadingFilters}
                        allowClear
                      >
                        <Option value="">Не выбрано</Option>
                        {priceTypes.map(type => (
                          <Option key={type.id} value={type.id}>
                            {type.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Диапазон цен">
                      <Row gutter={8}>
                        <Col span={12}>
                          <Form.Item
                            name={['criteria', 'prices', 'from']}
                            noStyle
                          >
                            <InputNumber
                              placeholder="От"
                              style={{ width: '100%' }}
                              min={0}
                              step={0.01}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name={['criteria', 'prices', 'to']}
                            noStyle
                          >
                            <InputNumber
                              placeholder="До"
                              style={{ width: '100%' }}
                              min={0}
                              step={0.01}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Form.Item>
                  </Col>
                </Row>
              </Panel>
            </Collapse>

            <Divider />

            <Card
              title="Предпросмотр XML"
              extra={
                <Button
                  icon={loadingPreview ? <Spin /> : <EyeOutlined />}
                  onClick={loadPreview}
                  disabled={loadingPreview}
                >
                  {feed?.url_token ? 'Обновить' : 'Сгенерировать'}
                </Button>
              }
            >
              {loadingPreview ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <Spin size="large" />
                  <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                    Генерация предпросмотра...
                  </Text>
                </div>
              ) : (
                <pre style={{
                  margin: 0,
                  padding: 16,
                  backgroundColor: '#f6f8fa',
                  borderRadius: 6,
                  maxHeight: 400,
                  overflow: 'auto',
                  fontSize: 12,
                  fontFamily: 'monospace',
                }}>
                  {previewXml}
                </pre>
              )}
            </Card>

            <Divider />

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Button
                onClick={() => {
                  updateJsonFromForm();
                  setActiveTab('json');
                }}
              >
                Перейти в JSON режим
              </Button>
              <Space>
                <Button onClick={onCancel} disabled={isSubmitting}>
                  Отмена
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  icon={<SaveOutlined />}
                >
                  {feed ? 'Сохранить изменения' : 'Создать фид'}
                </Button>
              </Space>
            </Space>
          </Form>
        </TabPane>

        <TabPane tab="JSON ввод" key="json">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={copyToClipboard}
              >
                {copied ? 'Скопировано' : 'Копировать'}
              </Button>
              <Upload
                beforeUpload={handleFileUpload}
                showUploadList={false}
                accept=".json"
              >
                <Button icon={<UploadOutlined />}>
                  Загрузить файл
                </Button>
              </Upload>
              <Button
                onClick={updateJsonFromForm}
              >
                Заполнить из формы
              </Button>
            </Space>

            {!jsonValid && (
              <Alert
                message="Невалидный JSON формат"
                description="Исправьте ошибки в JSON перед сохранением"
                type="error"
                showIcon
              />
            )}

            <TextArea
              value={jsonInput}
              onChange={(e) => handleJsonChange(e.target.value)}
              rows={15}
              style={{ fontFamily: 'monospace' }}
              placeholder={`{
  "name": "string",
  "description": "string",
  "root_tag": "string",
  "item_tag": "string",
  "field_tags": {
    "additionalProp1": "string",
    "additionalProp2": "string"
  },
  "criteria": {
    "warehouse_id": [0],
    "only_on_stock": true,
    "prices": {
      "from": 0,
      "to": 0
    },
    "category_id": [0],
    "price_types_id": 0
  },
  "tilda_url": "string",
  "tilda_username": "string",
  "tilda_password": "string",
  "tilda_sync_enabled": false,
  "tilda_sync_interval": 0,
  "tilda_price_id": "string",
  "tilda_discount_price_id": "string",
  "tilda_catalog_id": "string",
  "tilda_warehouse_id": "string"
}`}
            />

            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Text type={jsonValid ? "success" : "danger"}>
                {jsonValid ? '✓ JSON валиден' : '✗ Ошибка в JSON'}
              </Text>
              <Space>
                <Button
                  onClick={() => setActiveTab('simple')}
                >
                  Вернуться к форме
                </Button>
                <Button
                  type="primary"
                  onClick={submitJsonFeed}
                  loading={isSubmitting}
                  disabled={!jsonValid}
                  icon={<SaveOutlined />}
                >
                  {feed ? 'Сохранить изменения' : 'Создать фид'}
                </Button>
              </Space>
            </Space>
          </Space>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default FeedForm;